import { produce } from 'immer'
import { ActionType, getType } from 'typesafe-actions'

import { updateCleanAction } from 'app/fieldapp/store/cleans/actions'

import { rejectAssignmentAction } from '../assignments/actions'
import {
  setStandardAvailabilityDrawerStateAction,
  setVisitDrawerStateAction,
  setVisitTicketDrawerStateAction,
  setStandardCoveragePartnerDrawerStateAction,
  setCrossCoverageDrawerStateAction,
  setOnCallTaskDrawerStateAction,
} from './actions'
import { UiState } from './ui.types'

export const initialState: UiState = {
  drawers: {
    CrossCoverageDrawer: {
      crossCoverageId: undefined,
      forceClose: false,
      isOpen: false,
    },
    onCallTaskDrawer: { coverageId: '', forceClose: false, isOpen: false },
    standardAvailabilityDrawer: { forceClose: false, isOpen: false },
    standardCoveragePartnerDrawer: { forceClose: false, isOpen: false },
    visitDrawer: {
      forceClose: false,
      isOpen: false,
      tab: 'unitInfo',
      visitId: '',
    },
    visitTicketDrawer: { forceClose: false, isOpen: false, ticketId: '' },
  },
  modals: {
    cleanRejection: {
      isRejecting: false,
    },
  },
}

const actions = {
  rejectAssignmentAction,
  setCrossCoverageDrawerStateAction,
  setOnCallTaskDrawerStateAction,
  setStandardAvailabilityDrawerStateAction,
  setStandardCoveragePartnerDrawerStateAction,
  setVisitDrawerStateAction,
  setVisitTicketDrawerStateAction,
  updateCleanAction,
}

type UiReducerActionsTypes = ActionType<typeof actions>

export const uiReducer = (
  state = initialState,
  action: UiReducerActionsTypes,
): UiState =>
  produce(state, (draft: UiState) => {
    switch (action.type) {
      case getType(setVisitDrawerStateAction): {
        const prevDrawerState = state.drawers.visitDrawer
        const {
          isOpen = prevDrawerState.isOpen,
          tab = prevDrawerState.tab || 'unitInfo',
          visitId = prevDrawerState.visitId,
        } = action.payload

        draft.drawers.visitDrawer.isOpen = isOpen
        draft.drawers.visitDrawer.tab = tab
        draft.drawers.visitDrawer.visitId = visitId
        return
      }

      case getType(setVisitTicketDrawerStateAction): {
        const { isOpen, forceClose, ticketId = '' } = action.payload

        if (forceClose !== undefined) {
          draft.drawers.visitTicketDrawer.forceClose = forceClose
        }

        if (isOpen !== undefined) {
          draft.drawers.visitTicketDrawer.isOpen = isOpen
        }

        draft.drawers.visitTicketDrawer.ticketId = ticketId
        return
      }

      case getType(setStandardAvailabilityDrawerStateAction): {
        const { isOpen, forceClose } = action.payload

        if (forceClose !== undefined) {
          draft.drawers.standardAvailabilityDrawer.forceClose = forceClose
        }

        if (isOpen !== undefined) {
          draft.drawers.standardAvailabilityDrawer.isOpen = isOpen
        }

        return
      }

      case getType(setOnCallTaskDrawerStateAction): {
        const { coverageId = '', isOpen, forceClose } = action.payload

        if (forceClose !== undefined) {
          draft.drawers.onCallTaskDrawer.forceClose = forceClose
        }

        if (isOpen !== undefined) {
          draft.drawers.onCallTaskDrawer.isOpen = isOpen
        }

        draft.drawers.onCallTaskDrawer.coverageId = coverageId
        return
      }

      case getType(setStandardCoveragePartnerDrawerStateAction): {
        const { isOpen, forceClose } = action.payload

        if (isOpen !== undefined) {
          draft.drawers.standardCoveragePartnerDrawer.isOpen = isOpen
        }

        if (forceClose !== undefined) {
          draft.drawers.standardCoveragePartnerDrawer.forceClose = forceClose
        }

        return
      }

      case getType(setCrossCoverageDrawerStateAction): {
        const {
          crossCoverageId = undefined,
          isOpen,
          forceClose,
        } = action.payload

        draft.drawers.CrossCoverageDrawer.crossCoverageId = crossCoverageId
        if (forceClose !== undefined) {
          draft.drawers.CrossCoverageDrawer.forceClose = forceClose
        }

        if (isOpen !== undefined) {
          draft.drawers.CrossCoverageDrawer.isOpen = isOpen
        }

        return
      }

      case getType(rejectAssignmentAction.request): {
        draft.modals.cleanRejection.isRejecting = true
        return
      }

      case getType(rejectAssignmentAction.failure):
      case getType(rejectAssignmentAction.success): {
        draft.modals.cleanRejection.isRejecting = false
        return
      }
    }
  })
